import { endpoints } from '../config/endpoints';

// Create a new character
export const createCharacter = async (characterData, userId) => {
  try {
    console.log('Creating character with data:', { userId, ...characterData });
    
    const response = await fetch(endpoints.api.characters, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify({
        action: 'create_character',
        user_id: userId,
        ...characterData
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Character creation failed with status ${response.status}:`, errorText);
      throw new Error(`Failed to create character (${response.status}): ${errorText}`);
    }

    const result = await response.json();
    console.log('Character created successfully:', result);
    return result;
  } catch (error) {
    console.error('Error creating character:', error);
    throw error;
  }
};

// List user's characters
export const listUserCharacters = async (userId) => {
  try {
    console.log('Fetching characters for user:', userId);
    
    const response = await fetch(endpoints.api.characters, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify({
        action: 'list_characters',
        user_id: userId
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Character listing failed with status ${response.status}:`, errorText);
      throw new Error(`Failed to list characters (${response.status}): ${errorText}`);
    }

    const data = await response.json();
    console.log('Characters fetched successfully:', data);
    return data.characters || [];
  } catch (error) {
    console.error('Error listing characters:', error);
    // Return empty array on error to prevent UI crashes
    return [];
  }
};

// Update a character
export const updateCharacter = async (characterId, characterData, userId) => {
  try {
    console.log('Updating character:', { characterId, userId, ...characterData });
    
    const response = await fetch(endpoints.api.characters, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify({
        action: 'update_character',
        character_id: characterId,
        user_id: userId,
        ...characterData
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Character update failed with status ${response.status}:`, errorText);
      throw new Error(`Failed to update character (${response.status}): ${errorText}`);
    }

    const result = await response.json();
    console.log('Character updated successfully:', result);
    return result;
  } catch (error) {
    console.error('Error updating character:', error);
    throw error;
  }
};

// Delete a character
export const deleteCharacter = async (characterId, userId) => {
  try {
    console.log('Deleting character:', { characterId, userId });
    
    const response = await fetch(endpoints.api.characters, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify({
        action: 'delete_character',
        character_id: characterId,
        user_id: userId
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Character deletion failed with status ${response.status}:`, errorText);
      throw new Error(`Failed to delete character (${response.status}): ${errorText}`);
    }

    const result = await response.json();
    console.log('Character deleted successfully:', result);
    return result;
  } catch (error) {
    console.error('Error deleting character:', error);
    throw error;
  }
};

// Helper function to get character display name
export const getCharacterDisplayName = (character) => {
  return character.name || 'Unnamed Character';
};

// Helper function to check if a bot_id is a custom character
export const isCustomCharacter = (botId) => {
  return typeof botId === 'string' && botId.startsWith('custom_');
};

// Helper function to get character avatar
export const getCharacterAvatar = (botId) => {
  // This function can be used to determine avatar paths
  // Returns the appropriate image path based on bot_id
  if (botId === "0") {
    return "jarvis"; // Jarvis
  } else if (botId === "1") {
    return "samantha"; // Samantha  
  } else if (typeof botId === 'string' && botId.startsWith('custom_')) {
    return "placeholder"; // Custom characters use placeholder
  } else {
    return "samantha"; // Default fallback
  }
};

// Upload character image
export const uploadCharacterImage = async (characterId, userId, imageData, imageType) => {
  try {
    console.log('Uploading image for character:', { characterId, userId, imageType });
    console.log('Image data structure:', imageData);
    
    const payload = {
      action: 'upload_image',
      character_id: characterId,
      user_id: userId,
      image_data: imageData.base64,
      image_type: imageType,
      filename: imageData.name
    };
    
    console.log('Upload payload:', payload);
    console.log('Calling endpoint:', endpoints.api.characters);
    
    const response = await fetch(endpoints.api.characters, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Image upload failed with status ${response.status}:`, errorText);
      throw new Error(`Failed to upload image (${response.status}): ${errorText}`);
    }

    const result = await response.json();
    console.log('Image uploaded successfully:', result);
    return result;
  } catch (error) {
    console.error('Error uploading character image:', error);
    throw error;
  }
};

// Get character images
export const getCharacterImages = async (characterId, userId) => {
  try {
    console.log('Getting images for character:', { characterId, userId });
    
    const response = await fetch(endpoints.api.characters, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify({
        action: 'get_images',
        character_id: characterId,
        user_id: userId
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Get images failed with status ${response.status}:`, errorText);
      throw new Error(`Failed to get images (${response.status}): ${errorText}`);
    }

    const result = await response.json();
    console.log('Images retrieved successfully:', result);
    return result;
  } catch (error) {
    console.error('Error getting character images:', error);
    throw error;
  }
};

// Delete character image
export const deleteCharacterImage = async (characterId, userId, imageId) => {
  try {
    console.log('Deleting image for character:', { characterId, userId, imageId });
    
    const response = await fetch(endpoints.api.characters, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify({
        action: 'delete_image',
        character_id: characterId,
        user_id: userId,
        image_id: imageId
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Delete image failed with status ${response.status}:`, errorText);
      throw new Error(`Failed to delete image (${response.status}): ${errorText}`);
    }

    const result = await response.json();
    console.log('Image deleted successfully:', result);
    return result;
  } catch (error) {
    console.error('Error deleting character image:', error);
    throw error;
  }
};

// Character Discovery Board Functions

// Get all public characters (no authentication required)
export const getPublicCharacters = async () => {
  try {
    console.log('Fetching public characters from discovery board');
    
    const response = await fetch(endpoints.api.publicCharacters, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify({
        action: 'get_public_characters'
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Public characters fetch failed with status ${response.status}:`, errorText);
      throw new Error(`Failed to fetch public characters (${response.status}): ${errorText}`);
    }

    const data = await response.json();
    console.log('Public characters fetched successfully:', data);
    return data.public_characters || [];
  } catch (error) {
    console.error('Error fetching public characters:', error);
    // Return empty array on error to prevent UI crashes
    return [];
  }
};

// Toggle character visibility (publish/unpublish)
export const toggleCharacterVisibility = async (characterId, userId, isPublic) => {
  try {
    console.log('Toggling character visibility:', { characterId, userId, isPublic });
    
    const response = await fetch(endpoints.api.characters, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify({
        action: 'toggle_character_visibility',
        character_id: characterId,
        user_id: userId,
        is_public: isPublic
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Toggle visibility failed with status ${response.status}:`, errorText);
      throw new Error(`Failed to toggle character visibility (${response.status}): ${errorText}`);
    }

    const result = await response.json();
    console.log('Character visibility toggled successfully:', result);
    return result;
  } catch (error) {
    console.error('Error toggling character visibility:', error);
    throw error;
  }
};

// Mark character as opened by user (track user-character relationship)
export const openCharacter = async (userId, characterId) => {
  try {
    console.log('Opening character:', { userId, characterId });
    
    const response = await fetch(endpoints.api.characters, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify({
        action: 'open_character',
        user_id: userId,
        character_id: characterId
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Open character failed with status ${response.status}:`, errorText);
      throw new Error(`Failed to open character (${response.status}): ${errorText}`);
    }

    const result = await response.json();
    console.log('Character opened successfully:', result);
    return result;
  } catch (error) {
    console.error('Error opening character:', error);
    throw error;
  }
};

// Get user's opened characters (for sidebar display)
export const getUserOpenedCharacters = async (userId) => {
  try {
    console.log('Fetching user opened characters:', userId);
    
    const response = await fetch(endpoints.api.characters, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify({
        action: 'get_user_opened_characters',
        user_id: userId
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Get opened characters failed with status ${response.status}:`, errorText);
      throw new Error(`Failed to get opened characters (${response.status}): ${errorText}`);
    }

    const data = await response.json();
    console.log('User opened characters fetched successfully:', data);
    return data.opened_characters || [];
  } catch (error) {
    console.error('Error fetching user opened characters:', error);
    // Return empty array on error to prevent UI crashes
    return [];
  }
};

// Open character chat (navigate to chat interface and track opening)
export const openCharacterChat = async (userId, characterId) => {
  try {
    console.log('Opening character chat:', { userId, characterId });
    
    // First, mark the character as opened
    await openCharacter(userId, characterId);
    
    // Navigate to the appropriate chat interface
    let chatUrl;
    if (characterId === "0") {
      chatUrl = "/myai"; // Jarvis
    } else if (characterId === "1") {
      chatUrl = "/myai1"; // Samantha
    } else if (isCustomCharacter(characterId)) {
      chatUrl = `/myai?bot_id=${characterId}`; // Custom character with bot_id parameter
    } else {
      chatUrl = "/myai1"; // Default fallback
    }
    
    console.log('Navigating to chat URL:', chatUrl);
    
    // Use window.location for navigation
    window.location.href = chatUrl;
    
    return { success: true, chatUrl, characterId };
  } catch (error) {
    console.error('Error opening character chat:', error);
    throw error;
  }
};

// Check unified subscription status
export const checkUnifiedSubscription = async (user) => {
  try {
    // Import subscription utils dynamically to avoid circular dependencies
    const { getSubscriptionStatus } = await import('./subscriptionUtils');
    
    console.log('Checking unified subscription status for user');
    
    // Get subscription status for bot_id "1" (Sam subscription)
    const subscriptionStatus = await getSubscriptionStatus(user, "1");
    
    console.log('Unified subscription status:', subscriptionStatus);
    
    return {
      hasActiveSubscription: subscriptionStatus.canSendMessages,
      subscriptionType: subscriptionStatus.type,
      message: subscriptionStatus.message,
      canAccessChat: subscriptionStatus.canAccessChat,
      canSendMessages: subscriptionStatus.canSendMessages
    };
  } catch (error) {
    console.error('Error checking unified subscription:', error);
    
    // Return safe defaults on error
    return {
      hasActiveSubscription: false,
      subscriptionType: 'error',
      message: 'Unable to check subscription status',
      canAccessChat: !!user, // Can access if logged in
      canSendMessages: false
    };
  }
};